<template>
  <!-- Componente oculto na aplicação; o utilitário imprimirPagedJs clona o
       conteúdo e força display:block no clone para o Paged.js paginar. -->
  <div class="rp-ia-relatorio" style="display: none;">
    <!-- Cabeçalho -->
    <div class="rp-header">
      <div class="rp-header-top">
        <span class="rp-header-marca">CTech - ERP Comex</span>
        <span class="rp-header-data">{{ dataGeracao }}</span>
      </div>
      <h3 class="rp-titulo">Relatório de Consumo de IA</h3>
      <p class="rp-subtitulo">Período e filtros aplicados</p>
      <p class="rp-filtros">{{ textoFiltros }}</p>
    </div>

    <!-- Cards de resumo -->
    <div class="rp-cards">
      <div class="rp-card-resumo">
        <span class="rp-card-label">Total Tokens Input</span>
        <span class="rp-card-valor">{{ totalTokensInput.toLocaleString('pt-BR') }}</span>
      </div>
      <div class="rp-card-resumo">
        <span class="rp-card-label">Total Tokens Output</span>
        <span class="rp-card-valor">{{ totalTokensOutput.toLocaleString('pt-BR') }}</span>
      </div>
      <div class="rp-card-resumo">
        <span class="rp-card-label">Custo Total Estimado</span>
        <span class="rp-card-valor">{{ formatarCusto(totalCusto) }}</span>
      </div>
      <div class="rp-card-resumo">
        <span class="rp-card-label">Total de Sessões</span>
        <span class="rp-card-valor">{{ totalSessoes.toLocaleString('pt-BR') }}</span>
      </div>
      <div class="rp-card-resumo">
        <span class="rp-card-label">Custo Médio por Sessão</span>
        <span class="rp-card-valor">{{ custoMedioSessao !== null ? formatarCusto(custoMedioSessao) : 'N/A' }}</span>
      </div>
    </div>

    <!-- Tabela de registros -->
    <div class="rp-card">
      <div class="rp-card-header">Registros de Consumo</div>
      <div class="rp-card-body">
        <table class="rp-table">
          <thead>
            <tr>
              <th>Data/Hora</th>
              <th>Empresa</th>
              <th>Aplicação</th>
              <th>Provedor</th>
              <th>Modelo</th>
              <th>Usuário</th>
              <th>Sessão</th>
              <th class="text-end">Tokens Input</th>
              <th class="text-end">Tokens Output</th>
              <th class="text-end">Custo Estimado</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(log, i) in dados" :key="i">
              <td>{{ formatarDataHoraBR(log.tsCriacao) }}</td>
              <td>{{ log.nomeEmpresa || (log.idEmpresa ? `ID ${log.idEmpresa}` : '-') }}</td>
              <td>{{ log.origemTexto || '-' }}</td>
              <td>{{ obterNomeProvedor(log.lstProvider) }}</td>
              <td>{{ log.model || '-' }}</td>
              <td>{{ log.nomeUsuario || '-' }}</td>
              <td>{{ log.idSessao || '-' }}</td>
              <td class="text-end">{{ (log.tokensInput || 0).toLocaleString('pt-BR') }}</td>
              <td class="text-end">{{ (log.tokensOutput || 0).toLocaleString('pt-BR') }}</td>
              <td class="text-end">{{ formatarCusto(log.custoEstimado) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="7" class="text-end fw-bold">TOTAIS</td>
              <td class="text-end fw-bold">{{ totalTokensInput.toLocaleString('pt-BR') }}</td>
              <td class="text-end fw-bold">{{ totalTokensOutput.toLocaleString('pt-BR') }}</td>
              <td class="text-end fw-bold">{{ formatarCusto(totalCusto) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <p class="rp-total-registros">Total de registros: <strong>{{ dados.length }}</strong></p>

    <p class="rp-rodape">Relatório gerado automaticamente pelo sistema CTech - ERP Comex</p>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { formatarDataBR, formatarDataHoraBR } from '@/utils/useDataBR'
import { useMetadataListasStore } from '@/stores/metadataListas'

const props = defineProps({
  dados: { type: Array, default: () => [] },
  filtros: { type: Object, default: () => ({}) }
})

const metadataStore = useMetadataListasStore()
const listaProvedores = ref([])

onMounted(async () => {
  listaProvedores.value = await metadataStore.getListaLookup('CONFIG_PROVIDERS_IA', 'Sistema') || []
})

function obterNomeProvedor (id) {
  if (!id) return '-'
  const item = listaProvedores.value.find(p => String(p.id) === String(id))
  return item?.textoLista || id
}

const formatarCusto = (valor) => {
  const n = Number(valor) || 0
  return n.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 8,
    maximumFractionDigits: 8
  })
}

const dataGeracao = computed(() => formatarDataHoraBR(new Date()))

const totalTokensInput = computed(() =>
  props.dados.reduce((acc, l) => acc + (Number(l.tokensInput) || 0), 0)
)
const totalTokensOutput = computed(() =>
  props.dados.reduce((acc, l) => acc + (Number(l.tokensOutput) || 0), 0)
)
const totalCusto = computed(() =>
  props.dados.reduce((acc, l) => acc + (Number(l.custoEstimado) || 0), 0)
)
const totalSessoes = computed(() => {
  const sessoes = new Set()
  props.dados.forEach((l) => {
    if (l.idSessao) sessoes.add(l.idSessao)
  })
  return sessoes.size
})
const custoMedioSessao = computed(() =>
  totalSessoes.value > 0 ? totalCusto.value / totalSessoes.value : null
)

const textoFiltros = computed(() => {
  const f = props.filtros || {}
  const linhas = []
  if (f.empresa) linhas.push(`Empresa: ${f.empresa}`)
  if (f.aplicacao) linhas.push(`Aplicação: ${f.aplicacao}`)
  if (f.usuario) linhas.push(`Usuário: ${f.usuario}`)
  if (f.idSessao) linhas.push(`Sessão: ${f.idSessao}`)
  if (f.dataInicio) linhas.push(`Data Início: ${formatarDataBR(f.dataInicio)}`)
  if (f.dataFim) linhas.push(`Data Fim: ${formatarDataBR(f.dataFim)}`)
  return linhas.length > 0 ? linhas.join(' | ') : 'Nenhum filtro aplicado'
})
</script>

<style scoped>
.rp-ia-relatorio {
  background: #ffffff;
  color: #212529;
  font-family: 'Segoe UI', Arial, sans-serif;
  font-size: 10px;
}

.rp-header {
  border-bottom: 3px solid #34495e;
  padding-bottom: 10px;
  margin-bottom: 14px;
}

.rp-header-top {
  display: flex;
  justify-content: space-between;
  font-size: 8px;
  color: #6c757d;
  margin-bottom: 6px;
}

.rp-titulo {
  font-size: 16px;
  font-weight: 700;
  color: #34495e;
  margin: 0 0 4px 0;
}

.rp-subtitulo {
  font-size: 10px;
  color: #6c757d;
  margin: 0 0 2px 0;
}

.rp-filtros {
  font-size: 9px;
  font-style: italic;
  color: #495057;
  margin: 0;
}

.rp-cards {
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
}

.rp-card-resumo {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 8px 6px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  text-align: center;
}

.rp-card-label {
  font-size: 8px;
  font-weight: 600;
  color: #6c757d;
}

.rp-card-valor {
  font-size: 13px;
  font-weight: 700;
  color: #34495e;
}

.rp-card {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  margin-bottom: 12px;
}

.rp-card-header {
  background: #f8f9fa;
  padding: 8px 12px;
  font-weight: 600;
  font-size: 10px;
  border-bottom: 1px solid #dee2e6;
}

.rp-card-body {
  padding: 0;
}

.rp-table {
  width: 100%;
  border-collapse: collapse;
}

.rp-table thead th {
  background: #34495e;
  color: #ffffff;
  padding: 5px 6px;
  font-size: 8px;
  text-align: left;
}

.rp-table tbody td {
  padding: 4px 6px;
  font-size: 8px;
  border-bottom: 1px solid #dee2e6;
}

.rp-table tbody tr:nth-child(even) {
  background: #f8f9fa;
}

.rp-table tfoot td {
  padding: 5px 6px;
  font-size: 8px;
  background: #f0f0f0;
  border-top: 1px solid #dee2e6;
}

.rp-total-registros {
  font-size: 9px;
  margin: 8px 0 0 0;
}

.rp-rodape {
  font-size: 7px;
  color: #adb5bd;
  text-align: center;
  margin-top: 12px;
}
</style>
